import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  ItemContent,
  cn,
} from "@aircall/ds";
import {
  type ComponentPropsWithoutRef,
  type ForwardedRef,
  forwardRef,
} from "react";

import { getInitials } from "@/helpers/string/string.helpers";

export interface UserNameProps extends ComponentPropsWithoutRef<"div"> {}

export interface UserNameContentProps extends ComponentPropsWithoutRef<"div"> {}

export interface UserNameLabelProps extends ComponentPropsWithoutRef<"span"> {}

export interface UserNameDetailProps extends ComponentPropsWithoutRef<"span"> {}

export interface UserNameAvatarProps
  extends ComponentPropsWithoutRef<typeof Avatar> {
  /** Full name used to derive the avatar initials fallback. */
  name?: string;
  /** Optional image source for the avatar. */
  src?: string;
}

/**
 * Composable user identity row. Lay out an avatar alongside a text stack:
 *
 * ```tsx
 * <UserName>
 *   <SpeakerAvatar channel={channel} />
 *   <UserNameContent>
 *     <UserNameLabel>{fullName}</UserNameLabel>
 *     <UserNameDetail>{phoneNumber}</UserNameDetail>
 *   </UserNameContent>
 * </UserName>
 * ```
 *
 * The avatar slot is just `children`, so any avatar (DS Avatar, SpeakerAvatar,
 * or the bundled `UserNameAvatar`) can be dropped in and rearranged.
 */
export const UserName = forwardRef<HTMLDivElement, UserName.Props>(
  (
    componentProps: UserName.Props,
    forwardRef: ForwardedRef<HTMLDivElement>
  ) => {
    const { className, children, ...props } = componentProps;

    return (
      <div
        ref={forwardRef}
        data-slot="user-name"
        className={cn("flex items-center gap-1", className)}
        {...props}
      >
        {children}
      </div>
    );
  }
);
UserName.displayName = "UserName";

export namespace UserName {
  export type Props = UserNameProps;
}

/**
 * Vertical text stack placed next to the avatar (label + detail).
 *
 * Composes the DS `ItemContent` subpart for the column layout, overriding its
 * `data-slot` back to `user-name-content` and forcing the compact `flex flex-col`
 * stack so the public contract (slot name, props, layout) is unchanged.
 */
export const UserNameContent = forwardRef<
  HTMLDivElement,
  UserNameContent.Props
>(
  (
    componentProps: UserNameContent.Props,
    forwardRef: ForwardedRef<HTMLDivElement>
  ) => {
    const { className, children, ...props } = componentProps;

    return (
      <ItemContent
        ref={forwardRef}
        data-slot="user-name-content"
        className={cn("flex flex-col", className)}
        {...props}
      >
        {children}
      </ItemContent>
    );
  }
);
UserNameContent.displayName = "UserNameContent";

export namespace UserNameContent {
  export type Props = UserNameContentProps;
}

/** Primary identity line (the person's name). */
export const UserNameLabel = forwardRef<HTMLSpanElement, UserNameLabel.Props>(
  (
    componentProps: UserNameLabel.Props,
    forwardRef: ForwardedRef<HTMLSpanElement>
  ) => {
    const { className, children, ...props } = componentProps;

    return (
      <span
        ref={forwardRef}
        data-slot="user-name-label"
        className={cn("text-sm font-medium", className)}
        {...props}
      >
        {children}
      </span>
    );
  }
);
UserNameLabel.displayName = "UserNameLabel";

export namespace UserNameLabel {
  export type Props = UserNameLabelProps;
}

/** Secondary, muted line (e.g. a formatted phone number). */
export const UserNameDetail = forwardRef<HTMLSpanElement, UserNameDetail.Props>(
  (
    componentProps: UserNameDetail.Props,
    forwardRef: ForwardedRef<HTMLSpanElement>
  ) => {
    const { className, children, ...props } = componentProps;

    return (
      <span
        ref={forwardRef}
        data-slot="user-name-detail"
        className={cn("text-xs font-normal text-muted-foreground", className)}
        {...props}
      >
        {children}
      </span>
    );
  }
);
UserNameDetail.displayName = "UserNameDetail";

export namespace UserNameDetail {
  export type Props = UserNameDetailProps;
}

/**
 * Convenience DS Avatar whose fallback shows the initials derived from `name`.
 * Optional: callers with a richer avatar (e.g. SpeakerAvatar) pass that instead.
 */
export const UserNameAvatar = forwardRef<HTMLDivElement, UserNameAvatar.Props>(
  (
    componentProps: UserNameAvatar.Props,
    forwardRef: ForwardedRef<HTMLDivElement>
  ) => {
    const { name, src, className, ...props } = componentProps;
    const [firstName = "", lastName = ""] = (name ?? "").trim().split(" ");

    return (
      <Avatar
        ref={forwardRef}
        size="sm"
        data-slot="user-name-avatar"
        className={className}
        {...props}
      >
        {src ? <AvatarImage src={src} alt={name} /> : null}
        <AvatarFallback>{getInitials(firstName, lastName)}</AvatarFallback>
      </Avatar>
    );
  }
);
UserNameAvatar.displayName = "UserNameAvatar";

export namespace UserNameAvatar {
  export type Props = UserNameAvatarProps;
}
